import { useMemo, useState } from "react";
import moment from "moment";
import UIkit from "uikit";
import { Image } from "uikit-react";

const DOWS = [
  { id: 1, short: "Sun", label: "Sunday" },
  { id: 2, short: "Mon", label: "Monday" },
  { id: 3, short: "Tue", label: "Tuesday" },
  { id: 4, short: "Wed", label: "Wednesday" },
  { id: 5, short: "Thu", label: "Thursday" },
  { id: 6, short: "Fri", label: "Friday" },
  { id: 7, short: "Sat", label: "Saturday" },
];

const wdFormat = "YYYY-MM-DD HH:mm:ss";

const getDayPrefix = (dow) => `2001-01-0${dow}`;

const makeTimeOptions = (stepMins = 15) => {
  const opts = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += stepMins) {
      opts.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return opts;
};

const timeOptionsStart = makeTimeOptions(15);
const timeOptionsEnd = [...makeTimeOptions(15), "23:59"];

const defaultBulkSchedule = [
  { weekday: 2, start_time: "2001-01-02 09:00:00", end_time: "2001-01-02 17:00:00" },
  { weekday: 3, start_time: "2001-01-03 09:00:00", end_time: "2001-01-03 17:00:00" },
  { weekday: 4, start_time: "2001-01-04 09:00:00", end_time: "2001-01-04 17:00:00" },
  { weekday: 5, start_time: "2001-01-05 09:00:00", end_time: "2001-01-05 17:00:00" },
  { weekday: 6, start_time: "2001-01-06 09:00:00", end_time: "2001-01-06 17:00:00" },
];

const hhmmToLabel = (hhmm) => moment(hhmm, "HH:mm").format("h:mma");

const parseWd = (v) => {
  if (!v) return moment.invalid();
  return moment(String(v).trim(), wdFormat, true);
};

const BulkHours = ({
  selectedRecipients = [],
  inbox,
  setSelectedRecipients,
  inboxCtx,
}) => {
  const [bulkSchedule, setBulkSchedule] = useState([]);
  const [bulkSaving, setBulkSaving] = useState(false);

  const selectedUsers = useMemo(() => {
    return (inbox?.recipients || []).filter((r) =>
      selectedRecipients.includes(r.recipient_id)
    );
  }, [inbox, selectedRecipients]);

  const open = () => {
    if (!selectedRecipients.length) return;

    if (selectedUsers.length === 1) {
      setBulkSchedule(JSON.parse(JSON.stringify(selectedUsers[0].schedule || [])));
    } else {
      setBulkSchedule(JSON.parse(JSON.stringify(defaultBulkSchedule)));
    }

    UIkit.offcanvas("#team-bulk-hours")?.show();
  };

  const getFilteredEndTimes = (startTime) => {
    if (!startTime) return timeOptionsEnd;

    const startMoment = moment(startTime, "HH:mm", true);
    if (!startMoment.isValid()) return timeOptionsEnd;
    const startMinutes = startMoment.hours() * 60 + startMoment.minutes();

    return timeOptionsEnd.filter((t) => {
      const endMoment = moment(t, "HH:mm", true);
      const endMinutes = endMoment.hours() * 60 + endMoment.minutes();
      return endMinutes > startMinutes;
    });
  };

  const getBulkDay = (dow) => {
    return (bulkSchedule || []).find((d) => +d?.weekday === +dow);
  };

  const setBulkDayTime = (dow, key, hhmm) => {
    const currentSchedule = Array.isArray(bulkSchedule) ? bulkSchedule : [];
    const existing = currentSchedule.find((d) => +d?.weekday === +dow);

    let wd = existing
      ? { ...existing }
      : {
          weekday: dow,
          start_time: `${getDayPrefix(dow)} 09:00:00`,
          end_time: `${getDayPrefix(dow)} 17:00:00`,
        };

    const nextLocal = moment(`${getDayPrefix(dow)} ${hhmm}:00`, wdFormat, true);

    if (key === "start_time") {
      const endLocal = parseWd(wd.end_time);
      wd.start_time = nextLocal.format(wdFormat);

      if (!endLocal.isValid() || !endLocal.isAfter(nextLocal)) {
        wd.end_time = nextLocal.clone().add(60, "minutes").format(wdFormat);
      }
    } else {
      const startLocal = parseWd(wd.start_time);
      wd.end_time = nextLocal.format(wdFormat);

      if (!startLocal.isValid() || !nextLocal.isAfter(startLocal)) {
        wd.start_time = nextLocal.clone().subtract(60, "minutes").format(wdFormat);
      }
    }

    const nextSchedule = existing
      ? currentSchedule.map((d) => (+d?.weekday === +dow ? wd : d))
      : [...currentSchedule, wd];

    setBulkSchedule(nextSchedule);
  };

  const setBulkNineToFive = (dow) => {
    const currentSchedule = Array.isArray(bulkSchedule) ? bulkSchedule : [];
    const existing = currentSchedule.find((d) => +d?.weekday === +dow);

    const updated = {
      ...(existing || { weekday: dow }),
      weekday: dow,
      start_time: `${getDayPrefix(dow)} 09:00:00`,
      end_time: `${getDayPrefix(dow)} 17:00:00`,
    };

    const nextSchedule = existing
      ? currentSchedule.map((d) => (+d?.weekday === +dow ? updated : d))
      : [...currentSchedule, updated];

    setBulkSchedule(nextSchedule);
  };

  const setBulkAllDay = (dow) => {
    const currentSchedule = Array.isArray(bulkSchedule) ? bulkSchedule : [];
    const existing = currentSchedule.find((d) => +d?.weekday === +dow);

    const updated = {
      ...(existing || { weekday: dow }),
      weekday: dow,
      start_time: `${getDayPrefix(dow)} 00:00:00`,
      end_time: `${getDayPrefix(dow)} 23:59:00`,
    };

    const nextSchedule = existing
      ? currentSchedule.map((d) => (+d?.weekday === +dow ? updated : d))
      : [...currentSchedule, updated];

    setBulkSchedule(nextSchedule);
  };

  const toggleBulkMute = (dow) => {
    const currentSchedule = Array.isArray(bulkSchedule) ? bulkSchedule : [];
    const exists = currentSchedule.some((d) => +d?.weekday === +dow);

    const nextSchedule = exists
      ? currentSchedule.filter((d) => +d?.weekday !== +dow)
      : [
          ...currentSchedule,
          {
            weekday: dow,
            start_time: `${getDayPrefix(dow)} 09:00:00`,
            end_time: `${getDayPrefix(dow)} 17:00:00`,
          },
        ];

    setBulkSchedule(nextSchedule);
  };

  const applyBulkToAllDays = (sourceDow) => {
    const currentSchedule = Array.isArray(bulkSchedule) ? bulkSchedule : [];
    const source = currentSchedule.find((d) => +d?.weekday === +sourceDow);

    if (!source) {
      UIkit.notification("Nothing to apply: this day is muted.", "warning");
      return;
    }

    const startHHMMSS = parseWd(source.start_time).isValid()
      ? parseWd(source.start_time).format("HH:mm:ss")
      : "09:00:00";

    const endHHMMSS = parseWd(source.end_time).isValid()
      ? parseWd(source.end_time).format("HH:mm:ss")
      : "17:00:00";

    const nextSchedule = DOWS.map((day) => ({
      weekday: day.id,
      start_time: `${getDayPrefix(day.id)} ${startHHMMSS}`,
      end_time: `${getDayPrefix(day.id)} ${endHHMMSS}`,
    }));

    setBulkSchedule(nextSchedule);
  };

  const saveBulkHours = async () => {
    if (!selectedRecipients.length) return;

    const invalid = (bulkSchedule || []).some((wd) => {
      const start = parseWd(wd.start_time);
      const end = parseWd(wd.end_time);
      return !start.isValid() || !end.isValid() || !end.isAfter(start);
    });

    if (invalid) {
      UIkit.notification("Please fix invalid hours before saving.", "error");
      return;
    }

    const normalizedSchedule = (bulkSchedule || [])
      .slice()
      .sort((a, b) => (+a.weekday || 0) - (+b.weekday || 0));

    setBulkSaving(true);
    try {
      await inboxCtx.bulkUpdateSchedules(
        selectedRecipients,
        normalizedSchedule
      );

      await inboxCtx.get();
      setSelectedRecipients([]);
      UIkit.offcanvas("#team-bulk-hours")?.hide();
      UIkit.notification("Hours updated successfully");
    } catch (e) {
      console.error("Bulk update hours failed", e);
      UIkit.notification("Failed to update hours", "error");
    } finally {
      setBulkSaving(false);
    }
  };

  return (
    <>
      <button
        className="uk-button hms-btn hms-white-btn"
        type="button"
        disabled={!selectedRecipients.length}
        onClick={open}
      >
       Bulk Update Hours
        {selectedRecipients.length > 0 ? ` (${selectedRecipients.length})` : ""}
      </button>

      <div
        id="team-bulk-hours"
        className="uk-offcanvas hms-modals"
        uk-offcanvas="flip: true; overlay: true"
      >
        <div className="uk-offcanvas-bar" style={{ width: "720px", maxWidth: "95vw" }}>
          <button className="uk-offcanvas-close uk-close-large" type="button" data-uk-close />

          <div className="uk-modal-header">
            <h2 className="uk-modal-title">Bulk Update Notification Schedule</h2>
          </div>

          <div className="uk-modal-body">
            <div className="uk-text-meta uk-margin-top">
              {selectedRecipients.length} selected user{selectedRecipients.length === 1 ? "" : "s"}
            </div>
            {DOWS.map((day) => {
              const dow = day.id;
              const wd = getBulkDay(dow);

              const st = wd && parseWd(wd.start_time).isValid()
                ? parseWd(wd.start_time).format("HH:mm")
                : "09:00";

              const et = wd && parseWd(wd.end_time).isValid()
                ? parseWd(wd.end_time).format("HH:mm")
                : "17:00";

              return (
                <div key={dow} className="uk-margin-small">
                  <div className="work-day-header uk-flex uk-flex-between uk-flex-middle uk-margin-small-bottom">
                    <div className="work-day-title uk-flex uk-flex-middle" style={{ gap: 6, flexWrap: "wrap" }}>
                      <span className="uk-text-bold">{day.label}:</span>
                      <span className="uk-text-meta">
                        {wd
                          ? `${parseWd(wd.start_time).format("h:mma")} - ${parseWd(wd.end_time).format("h:mma")}`
                          : "Muted"}
                      </span>
                    </div>

                    <div className="work-day-actions uk-flex uk-flex-middle" style={{ gap: 8, flexShrink: 0 }}>
                      <button type="button" className="download" onClick={() => setBulkNineToFive(dow)} uk-tooltip="9 to 5">
                        <Image src="/images/nine-to-five.svg" />
                      </button>
                      <button type="button" className="download" onClick={() => setBulkAllDay(dow)} uk-tooltip="All Day">
                        <Image src="/images/24-hours.svg" />
                      </button>
                      <button
                        type="button"
                        className="download"
                        onClick={() => applyBulkToAllDays(dow)}
                        uk-tooltip="Apply to All"
                        disabled={!wd}
                        style={{ opacity: wd ? 1 : 0.4 }}
                      >
                        <Image src="/images/copy-schedule.svg" />
                      </button>
                      <button type="button" className="download" onClick={() => toggleBulkMute(dow)} uk-tooltip="Day Off">
                        <Image src="/images/day-off.svg" />
                      </button>
                    </div>
                  </div>

                  {!!wd ? (
                    <div className="uk-flex uk-flex-middle uk-flex-wrap" style={{ gap: 12 }}>
                      <div>
                        <label className="uk-form-label uk-margin-small-right">Start</label>
                        <div className="uk-inline" style={{ minWidth: 160 }}>
                          <div key={`bulk-st-${dow}-${st}`} uk-form-custom="target: > * > span:first-child">
                            <select value={st} onChange={(e) => setBulkDayTime(dow, "start_time", e.target.value)}>
                              {timeOptionsStart.map((t) => (
                                <option key={`bulk-st-${dow}-${t}`} value={t}>
                                  {hhmmToLabel(t)}
                                </option>
                              ))}
                            </select>
                            <button className="uk-button uk-button-default" type="button" tabIndex="-1">
                              <span></span>
                              <span uk-icon="icon: chevron-down"></span>
                            </button>
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="uk-form-label uk-margin-small-right">End</label>
                        <div className="uk-inline" style={{ minWidth: 160 }}>
                          <div key={`bulk-et-${dow}-${st}-${et}`} uk-form-custom="target: > * > span:first-child">
                            <select value={et} onChange={(e) => setBulkDayTime(dow, "end_time", e.target.value)}>
                              {getFilteredEndTimes(st).map((t) => (
                                <option key={`bulk-et-${dow}-${t}`} value={t}>
                                  {hhmmToLabel(t)}
                                </option>
                              ))}
                            </select>
                            <button className="uk-button uk-button-default" type="button" tabIndex="-1">
                              <span></span>
                              <span uk-icon="icon: chevron-down"></span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="uk-text-muted">Notifications muted for this day.</div>
                  )}

                  {dow < 7 && <hr className="uk-margin-small" />}
                </div>
              );
            })}

            <div className="uk-margin-top uk-flex uk-flex-left">
              <button
                className="uk-button hms-btn hms-red-btn"
                type="button"
                onClick={saveBulkHours}
                disabled={bulkSaving}
              >
                {bulkSaving ? (
                  <>
                    <span uk-spinner="ratio: 0.6" className="uk-margin-small-right" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </button>

              <button
                className="uk-button hms-btn uk-offcanvas-close"
                type="button"
                disabled={bulkSaving}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BulkHours;
